import React, { useState } from 'react';
import { updateGoalReminder } from '../api/userGoalApi';
import '../Style/GoalReminderManager.css';

const REMINDER_DAYS = [
  { label: 'Mon', value: 'MONDAY' },
  { label: 'Tue', value: 'TUESDAY' },
  { label: 'Wed', value: 'WEDNESDAY' },
  { label: 'Thu', value: 'THURSDAY' },
  { label: 'Fri', value: 'FRIDAY' },
  { label: 'Sat', value: 'SATURDAY' },
  { label: 'Sun', value: 'SUNDAY' },
];

function GoalReminderManager({ goals = [], onClose, onReminderUpdated }) {
  const [localGoals, setLocalGoals] = useState(
    goals.map((goal) => ({
      ...goal,
      reminderEnabled: goal.reminderEnabled || false,
      reminderTime: goal.reminderTime || '',
      reminderDays: goal.reminderDays || [],
    })),
  );

  const [expandedGoalId, setExpandedGoalId] = useState(null);
  const [savingGoalId, setSavingGoalId] = useState(null);

  const updateLocalGoal = (goalId, updates) => {
    setLocalGoals((prev) =>
      prev.map((goal) => (goal.id === goalId ? { ...goal, ...updates } : goal)),
    );
  };

  const toggleDay = (goalId, dayValue) => {
    setLocalGoals((prev) =>
      prev.map((goal) => {
        if (goal.id !== goalId) return goal;

        const currentDays = goal.reminderDays || [];
        const updatedDays = currentDays.includes(dayValue)
          ? currentDays.filter((day) => day !== dayValue)
          : [...currentDays, dayValue];

        return {
          ...goal,
          reminderDays: updatedDays,
        };
      }),
    );
  };

  const selectEveryDay = (goalId) => {
    updateLocalGoal(goalId, {
      reminderDays: REMINDER_DAYS.map((day) => day.value),
    });
  };

  const clearDays = (goalId) => {
    updateLocalGoal(goalId, {
      reminderDays: [],
    });
  };

  const formatReminderSummary = (goal) => {
    if (!goal.reminderEnabled) return 'Reminder off';

    const days = goal.reminderDays || [];

    if (!goal.reminderTime) return 'Choose a time';

    if (days.length === 7) {
      return `${goal.reminderTime} · Everyday`;
    }

    if (days.length === 0) {
      return `${goal.reminderTime} · No days selected`;
    }

    const shortDays = days
      .map((day) => REMINDER_DAYS.find((d) => d.value === day)?.label)
      .filter(Boolean)
      .join(' ');

    return `${goal.reminderTime} · ${shortDays}`;
  };

  const handleToggleReminder = (goal) => {
    const nextValue = !goal.reminderEnabled;

    updateLocalGoal(goal.id, {
      reminderEnabled: nextValue,
      reminderTime: nextValue ? goal.reminderTime : '',
      reminderDays: nextValue ? goal.reminderDays || [] : [],
    });

    if (nextValue) {
      setExpandedGoalId(goal.id);
    }
  };

  const handleSave = async (goal) => {
    if (goal.reminderEnabled && !goal.reminderTime) {
      alert('Please choose a reminder time.');
      return;
    }

    if (goal.reminderEnabled && (!goal.reminderDays || goal.reminderDays.length === 0)) {
      alert('Please choose at least one reminder day.');
      return;
    }

    try {
      setSavingGoalId(goal.id);

      const updatedGoal = await updateGoalReminder(goal.id, {
        dailyHabit: goal.dailyHabit || false,
        reminderEnabled: goal.reminderEnabled,
        reminderTime: goal.reminderEnabled ? goal.reminderTime : null,
        reminderDays: goal.reminderEnabled ? goal.reminderDays : [],
      });

      if (onReminderUpdated) {
        onReminderUpdated(updatedGoal);
      }

      setExpandedGoalId(null);
    } catch (error) {
      console.error('Failed to update reminder:', error);
      alert('Failed to update reminder.');
    } finally {
      setSavingGoalId(null);
    }
  };

  return (
    <div className="reminder-modal-overlay" onClick={onClose}>
      <div
        className="reminder-modal-content compact-reminder-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="reminder-modal-header">
          <div>
            <h2>Reminder Settings</h2>
            <p>Set custom reminders for each goal.</p>
          </div>

          <button type="button" className="reminder-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="compact-reminder-list">
          {localGoals.length === 0 ? (
            <p className="empty-entries">No goals found.</p>
          ) : (
            localGoals.map((goal) => {
              const isExpanded = expandedGoalId === goal.id;

              return (
                <div
                  key={goal.id}
                  className={`compact-reminder-row ${isExpanded ? 'expanded' : ''}`}
                >
                  <div className="compact-reminder-main">
                    <button
                      type="button"
                      className="compact-reminder-info"
                      onClick={() => setExpandedGoalId(isExpanded ? null : goal.id)}
                    >
                      <span className="compact-goal-title">
                        {goal.goalTitle || goal.title || 'Untitled'}
                      </span>

                      <span
                        className={`compact-reminder-summary ${
                          goal.reminderEnabled ? 'active' : ''
                        }`}
                      >
                        {formatReminderSummary(goal)}
                      </span>
                    </button>

                    <label className="compact-switch">
                      <input
                        type="checkbox"
                        checked={goal.reminderEnabled}
                        onChange={() => handleToggleReminder(goal)}
                      />
                      <span className="compact-slider"></span>
                    </label>
                  </div>

                  {isExpanded && goal.reminderEnabled && (
                    <div className="compact-reminder-editor">
                      <div className="compact-time-row">
                        <label>Time</label>
                        <input
                          type="time"
                          value={goal.reminderTime || ''}
                          onChange={(e) =>
                            updateLocalGoal(goal.id, {
                              reminderTime: e.target.value,
                            })
                          }
                        />
                      </div>

                      <div className="compact-days-header">
                        <label>Repeat</label>

                        <div className="compact-day-actions">
                          <button type="button" onClick={() => selectEveryDay(goal.id)}>
                            Everyday
                          </button>
                          <button type="button" onClick={() => clearDays(goal.id)}>
                            Clear
                          </button>
                        </div>
                      </div>

                      <div className="compact-days-grid">
                        {REMINDER_DAYS.map((day) => (
                          <button
                            key={day.value}
                            type="button"
                            className={
                              goal.reminderDays?.includes(day.value)
                                ? 'compact-day active'
                                : 'compact-day'
                            }
                            onClick={() => toggleDay(goal.id, day.value)}
                          >
                            {day.label}
                          </button>
                        ))}
                      </div>

                      <div className="compact-editor-actions">
                        <button
                          type="button"
                          className="compact-cancel-btn"
                          onClick={() => setExpandedGoalId(null)}
                        >
                          Cancel
                        </button>

                        <button
                          type="button"
                          className="compact-save-btn"
                          disabled={savingGoalId === goal.id}
                          onClick={() => handleSave(goal)}
                        >
                          {savingGoalId === goal.id ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="reminder-modal-footer">
          <button className="done-btn" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default GoalReminderManager;
